import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Clock, DollarSign, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import ApiService from '../services/api';



interface Service {
  id: number;
  name: string;
  description: string;
  price: number;
  duration: number;
  category: string;
  features: string[];
  isActive: boolean;
  isPopular: boolean;
}

interface Package {
  id: number;
  name: string;
  description: string;
  originalPrice: number;
  discountedPrice: number;
  services: string[];
  isActive: boolean;
  displayOrder: number;
}

interface Category {
  id: number;
  name: string;
  displayName: string;
  icon: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
  backgroundImage?: string;
}

interface HomeSettings {
  id: number;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  isActive: boolean;
}

const Services: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [homeSettings, setHomeSettings] = useState<HomeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all'); // Default to 'all'
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadServices();
    loadHomeSettings();
    
    // Sayfa yüklendiğinde en üste scroll yap
    window.scrollTo(0, 0);
  }, []);

  // Services yüklendikten sonra paketleri yükle
  useEffect(() => {
    if (services.length > 0) {
      loadPackages();
    }
  }, [services]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getServices();
      console.log('Services API response:', response);
      
      // API response formatını kontrol et
      let servicesData = [];
      if (response && Array.isArray(response)) {
        servicesData = response;
      } else if (response && response.services && Array.isArray(response.services)) {
        servicesData = response.services;
      } else if (response && response.data && Array.isArray(response.data)) {
        servicesData = response.data;
      }
      
      console.log('Processed services data:', servicesData);
      
             // Sadece aktif hizmetleri filtrele ve features alanını düzelt
       const activeServices = servicesData.filter((service: Service) => service.isActive !== false).map((service: any) => {
         // features alanını güvenli hale getir
         let features = [];
         if (service.features) {
           if (Array.isArray(service.features)) {
             features = service.features;
           } else if (typeof service.features === 'string') {
             try {
               features = JSON.parse(service.features);
             } catch (e) {
               features = [];
             }
           }
         }
         
         return {
           ...service,
           features: Array.isArray(features) ? features : []
         };
       });
       
       setServices(activeServices);
      
      console.log('Active services:', activeServices);
    } catch (error) {
      console.error('Services load error:', error);
      // Hata durumunda fallback veriler
      setServices([
        {
          id: 1,
          name: "Klasik Manikür",
          description: "Geleneksel manikür teknikleri ile tırnaklarınızın bakımı",
          price: 150,
          duration: 60,
          category: "manicure",
          features: ["Tırnak kesimi", "Cilt bakımı", "Oje uygulaması"],
          isActive: true,
          isPopular: true
        },
        {
          id: 2,
          name: "Nail Art Tasarım",
          description: "Özel tasarımlar ve yaratıcı nail art uygulamaları",
          price: 250,
          duration: 90,
          category: "nail-art",
          features: ["Özel tasarım", "El boyama", "Dekoratif elementler"],
          isActive: true,
          isPopular: true
        },
        {
          id: 3,
          name: "Jel Oje Uygulaması",
          description: "Uzun süre dayanıklı jel oje uygulamaları",
          price: 200,
          duration: 75,
          category: "gel",
          features: ["Jel oje", "UV kurutma", "Uzun ömürlü"],
          isActive: true,
          isPopular: false
        },
        {
          id: 4,
          name: "Protez Tırnak",
          description: "Doğal görünümlü protez tırnak uygulamaları",
          price: 300,
          duration: 120,
          category: "special",
          features: ["Protez tırnak", "Şekillendirme", "Oje uygulaması"],
          isActive: true,
          isPopular: false
        },
        {
          id: 5,
          name: "Fransız Manikür",
          description: "Klasik ve modern Fransız manikür stilleri",
          price: 180,
          duration: 70,
          category: "manicure",
          features: ["Fransız ucu", "Doğal görünüm", "Elegant tasarım"],
          isActive: true,
          isPopular: false
        },
        {
          id: 6,
          name: "Pedikür",
          description: "Ayak bakımı ve pedikür hizmetleri",
          price: 200,
          duration: 80,
          category: "pedicure",
          features: ["Ayak bakımı", "Tırnak kesimi", "Masaj"],
          isActive: true,
          isPopular: false
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadPackages = async () => {
    try {
      const response = await ApiService.getPackages();
      console.log('Packages API response:', response);
      
      let packagesData = [];
      if (response && Array.isArray(response)) {
        packagesData = response;
      } else if (response && response.packages && Array.isArray(response.packages)) {
        packagesData = response.packages;
      }
      
      // Sadece aktif paketleri filtrele
      const activePackages = packagesData.filter((pkg: Package) => pkg.isActive !== false);
      
      // Hizmet isimlerini çözümle
      const packagesWithServiceNames = activePackages.map((pkg: Package) => {
        // Services alanını güvenli hale getir
        let serviceIds = [];
        if (pkg.services) {
          if (Array.isArray(pkg.services)) {
            serviceIds = pkg.services;
          } else if (typeof pkg.services === 'string') {
            try {
              serviceIds = JSON.parse(pkg.services);
            } catch (e) {
              serviceIds = [];
            }
          }
        }
        
        // Service ID'lerini string'e çevir ve service isimlerini bul
        const serviceNames = serviceIds.map((serviceId: any) => {
          const id = serviceId.toString();
          const service = services.find(s => s.id.toString() === id);
          return service ? service.name : `Hizmet ${id}`;
        });
        
        return {
          ...pkg,
          services: serviceNames
        };
      });
      
      setPackages(packagesWithServiceNames);
      
      console.log('Active packages with service names:', packagesWithServiceNames);
    } catch (error) {
      console.error('Packages load error:', error);
      setPackages([]);
    }
  };

  // Kategorileri yükle
  useEffect(() => {
    loadCategories();
  }, []);

  // Debug: Kategoriler state değişikliğini izle
  useEffect(() => {
    console.log('Categories state changed:', categories);
    console.log('Categories with background images:', categories.filter(cat => cat.backgroundImage));
    console.log('All categories:', categories.map(cat => ({ id: cat.id, name: cat.name, backgroundImage: cat.backgroundImage })));
  }, [categories]);

  const loadHomeSettings = async () => {
    try {
      const response = await ApiService.getHomeSettings();
      if (response && response.settings) {
        setHomeSettings(response.settings);
      }
    } catch (error) {
      console.error('Home settings load error:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await ApiService.getCategories();
      console.log('Categories API response:', response);
      let categoriesData = response.categories || [];
      console.log('Categories data:', categoriesData);
      
      // Paketler kategorisinin var olup olmadığını kontrol et
      const hasPackagesCategory = categoriesData.some((cat: any) => cat.name === 'packages');
      console.log('Has packages category:', hasPackagesCategory);
      
      // Eğer paketler kategorisi yoksa ekle
      if (!hasPackagesCategory) {
        console.log('Adding packages category manually');
        categoriesData.push({
          id: 999, // Yüksek ID numarası
          name: 'packages',
          displayName: 'PAKETLER',
          icon: '📦',
          isActive: true,
          displayOrder: 999 // En yüksek displayOrder
        });
      }
      
      setCategories(categoriesData);
    } catch (error) {
      console.error('Categories load error:', error);
      // Fallback kategoriler
      setCategories([
        { id: 2, name: 'manicure', displayName: 'MANİKÜR', icon: '💅', isActive: true, displayOrder: 1 },
        { id: 3, name: 'pedicure', displayName: 'PEDİKÜR', icon: '🦶', isActive: true, displayOrder: 2 },
        { id: 4, name: 'nail-art', displayName: 'NAİL ART', icon: '🎨', isActive: true, displayOrder: 3 },
        { id: 5, name: 'gel', displayName: 'JEL OJE', icon: '✨', isActive: true, displayOrder: 4 },
        { id: 6, name: 'special', displayName: 'ÖZEL', icon: '💎', isActive: true, displayOrder: 5 },
        { id: 7, name: 'repair', displayName: 'ONARIM', icon: '🔧', isActive: true, displayOrder: 6 },
        { id: 8, name: 'extensions', displayName: 'UZATMA', icon: '📏', isActive: true, displayOrder: 7 },
        { id: 9, name: 'packages', displayName: 'PAKETLER', icon: '📦', isActive: true, displayOrder: 8 }
      ]);
    }
  };

  const filteredServices = selectedCategory === 'all' 
    ? services.sort((a, b) => {
        // Önce popüler hizmetleri göster, sonra diğerlerini
        if (a.isPopular && !b.isPopular) return -1;
        if (!a.isPopular && b.isPopular) return 1;
        // Popülerlik durumu aynıysa ID'ye göre sırala
        return a.id - b.id;
      })
    : selectedCategory === 'packages'
    ? []
    : services.filter(service => service.category === selectedCategory).sort((a, b) => {
        // Kategori filtrelenmiş hizmetlerde de popüler olanları önce göster
        if (a.isPopular && !b.isPopular) return -1;
        if (!a.isPopular && b.isPopular) return 1;
        return a.id - b.id;
      });

  const filteredPackages = selectedCategory === 'packages'
    ? packages
    : selectedCategory === 'all'
    ? packages
    : [];

  // Aktif kategorileri filtrele ve sırala - Paketler en sonda olsun
  const activeCategories = categories
    .filter(cat => cat.isActive)
    .sort((a, b) => {
      // Paketler kategorisini en sona koy
      if (a.name === 'packages') return 1;
      if (b.name === 'packages') return -1;
      // Diğer kategorileri displayOrder'a göre sırala
      return a.displayOrder - b.displayOrder;
    });

  const getCategoryIcon = (category: string) => {
    // Önce veritabanından kategori ikonunu bul
    const categoryData = activeCategories.find(cat => cat.name === category);
    if (categoryData) {
      return categoryData.icon;
    }
    
    // Fallback ikonlar
    const iconMap: { [key: string]: string } = {
      'manicure': '💅',
      'pedicure': '🦶',
      'nail-art': '🎨',
      'gel': '✨',
      'special': '💎',
      'repair': '🔧',
      'extensions': '📏',
      'packages': '📦'
    };
    return iconMap[category] || '💅';
  };

  // Scroll kontrol fonksiyonları
  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      setIsAutoScrolling(false);
      scrollContainerRef.current.scrollBy({
        left: -320, // Card width + gap
        behavior: 'smooth'
      });
      setTimeout(() => setIsAutoScrolling(true), 5000); // 5 saniye sonra otomatik scroll'u tekrar başlat
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      setIsAutoScrolling(false);
      scrollContainerRef.current.scrollBy({
        left: 320, // Card width + gap
        behavior: 'smooth'
      });
      setTimeout(() => setIsAutoScrolling(true), 5000); // 5 saniye sonra otomatik scroll'u tekrar başlat
    }
  };

  // Otomatik scroll
  useEffect(() => {
    if (!isAutoScrolling || loading) return;

    const interval = setInterval(() => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        
        // Sona geldiysek başa dön
        if (scrollLeft >= scrollWidth - clientWidth - 1) {
          scrollContainerRef.current.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        } else {
          // Yavaşça sağa kaydır
          scrollContainerRef.current.scrollBy({
            left: 1,
            behavior: 'auto'
          });
        }
        updateScrollButtons();
      }
    }, 50); // 50ms'de bir pixel kaydır (yavaş hareket)

    return () => clearInterval(interval);
  }, [isAutoScrolling, loading]);

  // Scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons(); // İlk yüklemede kontrol et
      
      return () => container.removeEventListener('scroll', updateScrollButtons);
    }
  }, [filteredServices, filteredPackages]);

  // Touch/swipe event listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let startX = 0;
    let startY = 0;
    let isScrolling = false;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isScrolling = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isScrolling) {
        const deltaX = Math.abs(e.touches[0].clientX - startX);
        const deltaY = Math.abs(e.touches[0].clientY - startY);
        
        // Yatay kaydırma daha fazlaysa scroll'u durdur
        if (deltaX > deltaY) {
          isScrolling = true;
          setIsAutoScrolling(false);
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const deltaX = e.changedTouches[0].clientX - startX;
      const deltaY = Math.abs(e.changedTouches[0].clientY - startY);
      
      // Yatay kaydırma varsa ve dikey kaydırma azsa
      if (Math.abs(deltaX) > 50 && deltaY < 100) {
        // 5 saniye sonra otomatik scroll'u tekrar başlat
        setTimeout(() => setIsAutoScrolling(true), 5000);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  return (
    <div className="text-white min-h-screen" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
      {/* Hero Section */}
      <section className="relative py-4 sm:py-6 md:py-8 px-4">
        <div className="max-w-7xl mx-auto text-center" style={{ transform: 'translateY(40px)' }}>
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl font-light mb-4 sm:mb-6 tracking-wider" style={{ fontFamily: 'FuturaLTPaneuropean', color: '#9e906d' }}>
            HİZMETLERİMİZ
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-8 sm:mb-12 font-light max-w-3xl mx-auto px-4" style={{ color: '#000000' }}>
            "Beauty is power, and Sheva is your magic wand!"
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 sm:py-10 md:py-12 px-4 border-b border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4">
            {/* Tüm Hizmetler butonu */}
            <button
              onClick={() => setSelectedCategory('all')}
              className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 border transition-all duration-300 text-xs sm:text-sm tracking-wide"
              style={{
                borderColor: selectedCategory === 'all' ? '#ffffff' : '#9e906d',
                color: selectedCategory === 'all' ? '#ffffff' : '#9e906d',
                backgroundColor: selectedCategory === 'all' ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
              }}
            >
              TÜM HİZMETLER
            </button>
            
            {/* Diğer kategoriler */}
            {activeCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.name)}
                className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 border transition-all duration-300 text-xs sm:text-sm tracking-wide"
                style={{
                  borderColor: selectedCategory === category.name ? '#ffffff' : '#9e906d',
                  color: selectedCategory === category.name ? '#ffffff' : '#9e906d',
                  backgroundColor: selectedCategory === category.name ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
                }}
              >
                {category.displayName}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="relative">
              <div 
                ref={scrollContainerRef}
                className="overflow-x-hidden pb-4 px-4"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="flex gap-4 sm:gap-6" style={{ width: 'max-content' }}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="p-8 border border-gray-800 animate-pulse flex-shrink-0" style={{ 
                      backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937',
                      width: '300px',
                      minHeight: '400px'
                    }}>
                      <div className="w-16 h-16 bg-gray-700 rounded-full mb-6"></div>
                      <div className="h-6 bg-gray-700 rounded mb-4"></div>
                      <div className="h-4 bg-gray-700 rounded mb-4"></div>
                      <div className="h-4 bg-gray-700 rounded mb-6"></div>
                      <div className="h-8 bg-gray-700 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (filteredServices.length > 0 || filteredPackages.length > 0) ? (
            <div className="relative">
              {/* Sol Ok Butonu - Sadece Desktop */}
              {canScrollLeft && (
                <button
                  onClick={scrollLeft}
                  className="hidden md:block absolute left-2 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300"
                  style={{ backdropFilter: 'blur(4px)' }}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              
              {/* Sağ Ok Butonu - Sadece Desktop */}
              {canScrollRight && (
                <button
                  onClick={scrollRight}
                  className="hidden md:block absolute right-2 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300"
                  style={{ backdropFilter: 'blur(4px)' }}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
              
              <div 
                ref={scrollContainerRef}
                className="overflow-x-auto pb-4 px-4"
                style={{ 
                  scrollbarWidth: 'none', 
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                <div className="flex gap-4 sm:gap-6" style={{ width: 'max-content' }}>
              {/* Services */}
              {filteredServices.map((service) => (
                <div key={`service-${service.id}`} className="group p-4 sm:p-6 md:p-8 border border-gray-800 hover:border-gray-600 transition-all duration-300 relative overflow-hidden flex flex-col flex-shrink-0" style={{ 
                  backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937',
                  width: '300px',
                  minHeight: '400px'
                }}>
                  {/* Category Background Image */}
                  {(() => {
                    const categoryData = categories.find(cat => cat.name === service.category);
                    console.log('Service category:', service.category);
                    console.log('Found category data:', categoryData);
                    console.log('Category background image:', categoryData?.backgroundImage);
                    
                    return categoryData?.backgroundImage ? (
                      <div className="absolute inset-0">
                        <img
                          src={categoryData.backgroundImage}
                          alt={`${categoryData.displayName} background`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Background image failed to load:', categoryData.backgroundImage);
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-800/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    );
                  })()}
                  
                  {/* Category Badge */}
                  {(() => {
                    const categoryData = categories.find(cat => cat.name === service.category);
                    return categoryData ? (
                      <div className="absolute top-4 right-4 bg-gray-800/80 text-white px-3 py-1 rounded-full text-xs font-medium z-10">
                        {categoryData.displayName}
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Popular Badge */}
                  {service.isPopular && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium z-10 flex items-center">
                      <Star className="w-3 h-3 mr-1" />
                      POPÜLER
                    </div>
                  )}
                  
                  {/* Service Title */}
                  <h3 className="relative z-10 text-lg sm:text-xl md:text-2xl font-light mb-3 sm:mb-4 tracking-wide" style={{ color: '#f2eadd' }}>
                    {service.name}
                  </h3>
                  
                  {/* Service Description */}
                  <p className="relative z-10 text-gray-400 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                    {service.description}
                  </p>
                  
                  {/* Service Features */}
                  {service.features && Array.isArray(service.features) && service.features.length > 0 && (
                    <ul className="relative z-10 space-y-2 mb-6">
                      {service.features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-300">
                          <div className="w-1 h-1 bg-gray-500 rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {/* Service Details */}
                  <div className="relative z-10 flex items-center justify-between mb-4 sm:mb-6">
                    <div className="flex items-center text-gray-400">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="text-xs sm:text-sm">{service.duration} min</span>
                    </div>
                    <div className="text-lg sm:text-xl md:text-2xl font-light">
                      ₺{service.price}
                    </div>
                  </div>
                  
                  {/* Spacer to push button to bottom */}
                  <div className="flex-grow"></div>
                  
                  {/* Book Button */}
                  <Link 
                    to="/contact"
                    className="relative z-10 inline-flex items-center w-full justify-center px-4 sm:px-6 py-2 sm:py-3 border border-gray-600 text-white hover:bg-white hover:text-black transition-all duration-300 text-xs sm:text-sm tracking-wide group-hover:border-white mt-auto"
                  >
                    ŞİMDİ RANDEVU AL
                    <ArrowRight className="ml-1 sm:ml-2 w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
              ))}

              {/* Packages */}
              {filteredPackages.map((pkg) => (
                <div key={`package-${pkg.id}`} className="group bg-gradient-to-br from-purple-900/20 to-pink-900/20 p-8 border border-purple-600 hover:border-purple-400 transition-all duration-300 relative overflow-hidden flex-shrink-0 flex flex-col" style={{ 
                  width: '300px',
                  minHeight: '400px'
                }}>
                  {/* Background Pattern */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-800/10 to-pink-800/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Package Badge */}
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    PAKET
                  </div>
                  
                  {/* Package Title */}
                  <h3 className="relative z-10 text-2xl font-light mb-4 tracking-wide">
                    {pkg.name}
                  </h3>
                  
                  {/* Package Description */}
                  <p className="relative z-10 text-gray-300 mb-6 leading-relaxed">
                    {pkg.description}
                  </p>
                  
                  {/* Package Services */}
                  {pkg.services && Array.isArray(pkg.services) && pkg.services.length > 0 && (
                    <ul className="relative z-10 space-y-2 mb-6">
                      {pkg.services.map((service, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-300">
                          <div className="w-1 h-1 bg-purple-400 rounded-full mr-3"></div>
                          {service}
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {/* Package Pricing */}
                  <div className="relative z-10 flex items-center justify-between mb-6">
                    <div className="flex items-center text-gray-400">
                      <Package className="w-4 h-4 mr-2" />
                      <span className="text-sm">Paket</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-light text-purple-400">
                        ₺{pkg.discountedPrice}
                      </div>
                      <div className="text-sm text-gray-500 line-through">
                        ₺{pkg.originalPrice}
                      </div>
                    </div>
                  </div>
                  
                  {/* Spacer to push button to bottom */}
                  <div className="flex-grow"></div>
                  
                  {/* Book Button */}
                  <Link 
                    to="/contact"
                    className="relative z-10 inline-flex items-center w-full justify-center px-6 py-3 border border-purple-600 text-white hover:bg-purple-600 transition-all duration-300 text-sm tracking-wide group-hover:border-purple-400"
                  >
                    PAKETİ SATIN AL
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
              ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 sm:py-16 md:py-20 px-4">
              <div className="text-4xl sm:text-5xl md:text-6xl mb-4 sm:mb-6">💅</div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-light mb-3 sm:mb-4">Bu kategoride hizmet bulunamadı</h3>
              <p className="text-gray-400 text-sm sm:text-base">Lütfen diğer kategorileri kontrol edin veya daha fazla bilgi için bizimle iletişime geçin</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link 
              to="/contact"
              className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 text-base sm:text-lg tracking-wide"
            >
              RANDEVU AL
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link 
              to="/gallery"
              className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 border border-white text-white hover:bg-white hover:text-black transition-all duration-300 text-base sm:text-lg tracking-wide"
            >
              GALERİYİ GÖRÜNTÜLE
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 md:py-12 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-xs sm:text-sm">
            © 2025 Sheva Studio. Tüm Hakları Saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Services;